import { supabase, authReady } from './supabase';
import { SOURCE_BRANCH } from '../utils/deployInfo';
import { logConnectionError } from './analytics/eventLogger';

/**
 * Waitlist join → launch_waitlist row (exchange-clarity D10): the DB half of
 * the keep-promise. The analytics funnel event records the moment; THIS row
 * is the durable room tie (source_chat_room_id + contact), so conversion
 * healing can work even for an evicted joiner who never shared a phone
 * in-chat. Called fire-and-forget from LaunchSignupOverlay's submit — capture
 * never blocks on the network (never-block-confirmation rule); a failed write
 * logs to the error tier and the funnel event still records the signup.
 *
 * Table shape + anon INSERT policy verified on dev (refine pass 1) AND prod
 * (execution, 2026-07-27): email/phone/name/source_chat_room_id/
 * connected_with/branch/created_at.
 */
/**
 * Pure row builder — the contactType→column mapping and null-normalization,
 * separated from the network write so the shape is unit-testable.
 */
export const buildWaitlistRow = ({
  contactType,
  contactValue,
  name,
  chatRoomId,
  connectedWith,
  anonUserId,
}) => ({
  name: name || null,
  source_chat_room_id: chatRoomId || null,
  connected_with: connectedWith || null,
  // D7 bridge pointer: identity half — at real conversion the anon_aliases
  // machinery compiles every bridge row's identity into the one account
  anon_user_id: anonUserId || null,
  branch: SOURCE_BRANCH,
  [contactType === 'phone' ? 'phone' : 'email']: contactValue || null,
});

/**
 * Has this device ever joined the waitlist? The one reader of the marker
 * writeWaitlistJoin stamps below — surfaces that retire on joining (the SP
 * Get-app banner) call this instead of touching the key (review 2026-08-09:
 * writer and reader must share one key definition).
 */
export const hasJoinedWaitlist = () => localStorage.getItem(WAITLIST_JOINED_KEY) === 'true';

const WAITLIST_JOINED_KEY = 'waitlist_joined';

/**
 * Stamp the joined marker alone — the one writer of the key. writeWaitlistJoin
 * stamps it at a waitlist join; the new Simple Profile's confirm-number sheet
 * (signup E3a, SIGNUP_Anon §6e `joined`) stamps it when the number is
 * confirmed into pending, which is that path's join. Never throws.
 */
export const markWaitlistJoined = () => {
  try {
    localStorage.setItem(WAITLIST_JOINED_KEY, 'true');
  } catch (storageErr) {
    console.warn('[WAITLIST] Could not store joined marker:', storageErr);
  }
};

export const writeWaitlistJoin = async ({
  contactType,
  contactValue,
  name,
  chatRoomId,
  connectedWith,
  anonUserId,
}) => {
  // Durable local marker, stamped at the JOIN itself — surfaces that retire
  // on joining (the SP Get-app banner) read this at render time. It must NOT
  // ride the overlay's completion callbacks: every dismissal path (timer, X,
  // backdrop, navigation) had to be individually wired and three device
  // reports (B24/B26/B27) each found another unwired one. Set before the
  // network write on purpose — the service is fire-and-forget and the user's
  // intent counts from the tap (never-block-confirmation rule).
  markWaitlistJoined();
  try {
    await authReady;
    const row = buildWaitlistRow({
      contactType,
      contactValue,
      name,
      chatRoomId,
      connectedWith,
      anonUserId,
    });
    const { error } = await supabase.from('launch_waitlist').insert(row);
    if (error) throw error;
    console.log('[WAITLIST] Join written for', chatRoomId || '(no room)');
    return true;
  } catch (err) {
    console.error('[WAITLIST] Write failed:', err);
    logConnectionError('waitlist_write_failed', {
      chatRoomId: chatRoomId || null,
      message: err?.message || 'insert failed',
    });
    return false;
  }
};
